// Time off — ask for a day, see where it got to, and (if that is your job)
// answer somebody else's.
//
// Owner's request: employee vacation requests and sick time. One screen rather
// than two, because the question "am I off on the 12th" and the question "who
// is asking to be off" are the same list seen from two sides, and splitting
// them means a manager checking their OWN holiday has to go to a different
// place from where they approve everyone else's.
//
// ── What it does not do ────────────────────────────────────────────────────
//
// It shows no pay figure and computes none. An approved day records how many
// hours it is worth; what payroll does with that is money math and a separate
// human call (CLAUDE.md rule 4), so there is nothing on this screen to be
// wrong about somebody's wages. When that lands, it lands on the timesheet,
// which is where every other hours figure in this app already is.

import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FwButton, FwField, FwInput, FwSelect, FwTextarea, Notice, Pill,
  SectionLabel, EmptyState, Skeleton, Sheet,
} from '../../../ui/fw';
import { HoursField } from '../../shared/HoursField';
import { clockLabel } from '../../shared/hoursPicker';
import { get, post, del, describeError } from '../../shared/http';
import { useCan } from '../../../hooks/usePermission';
import './TimeOff.css';

/**
 * The kinds, mirroring server/utils/timeOff.js KINDS.
 *
 * Declared once and checked against the server by timeOffContract.test.js —
 * the shape this repo keeps arriving at (navigation.js, estimateWorkflow.js,
 * payCategories.js). Hardcoding the list in the markup is how a screen ends up
 * offering a button the server answers 400 to.
 */
export const TIME_OFF_KINDS = [
  { key: 'vacation', label: 'Vacation' },
  { key: 'sick', label: 'Sick' },
];

/** Status -> how it reads. Tones, not colour alone — see APP_REBUILD §3.3. */
const STATUS = {
  pending: { tone: 'warn', label: 'Waiting' },
  approved: { tone: 'ok', label: 'Approved' },
  denied: { tone: 'stop', label: 'Turned down' },
};

const KIND_LABEL = (k) => (TIME_OFF_KINDS.find((x) => x.key === k) || {}).label || k;

/** "5 Oct" / "5–7 Oct 2026". Built from the stored YYYY-MM-DD, never from a Date at midnight. */
export function rangeLabel(start, end) {
  if (!start) return '';
  const fmt = (s) => {
    const d = new Date(`${s}T12:00:00Z`);
    return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString(undefined, {
      day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
    });
  };
  return start === end ? fmt(start) : `${fmt(start)} – ${fmt(end)}`;
}

/** How many days a range covers, inclusive. Mirrors timeOff.daysInRange's length. */
export function dayCount(start, end) {
  if (!start || !end) return 0;
  const a = new Date(`${start}T12:00:00Z`);
  const b = new Date(`${end}T12:00:00Z`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b < a) return 0;
  return Math.round((b - a) / 86400000) + 1;
}

function RequestRow({ r, children }) {
  const s = STATUS[r.status] || { tone: 'neutral', label: r.status };
  const days = dayCount(r.start_date, r.end_date);
  return (
    <li className="to-row" data-time-off-row={r.id}>
      <div className="to-row__head">
        <span className="to-row__when">{rangeLabel(r.start_date, r.end_date)}</span>
        <Pill tone={s.tone}>{s.label}</Pill>
      </div>
      <div className="to-row__meta">
        <span className="to-row__kind">{KIND_LABEL(r.kind)}</span>
        {' · '}
        {/* The DAYS and the hours a day, never a total and never a figure with
            a currency on it. A total invites the reader to multiply it by a
            rate, which is the one sum this screen must not appear to do. */}
        <span className="fw-num">{days} {days === 1 ? 'day' : 'days'}</span>
        {r.hours_per_day != null ? (
          <>{' · '}<span className="fw-num">{clockLabel(r.hours_per_day)} a day</span></>
        ) : null}
      </div>
      {r.user_name ? <div className="to-row__who">{r.user_name}</div> : null}
      {r.reason ? <div className="to-row__reason">“{r.reason}”</div> : null}
      {r.status !== 'pending' ? (
        <div className="to-row__decision">
          {s.label}
          {r.decided_by_name ? ` by ${r.decided_by_name}` : ''}
          {r.decided_note ? ` — ${r.decided_note}` : ''}
        </div>
      ) : null}
      {children ? <div className="to-row__actions">{children}</div> : null}
    </li>
  );
}

export default function TimeOff() {
  const qc = useQueryClient();
  // The same authority that approves hours — see server/routes/timeOff.js.
  // Asked through useCan so a per-user grant reaches it, never off the role.
  //
  // BOTH hooks are called before either is used. `useCan(a) || useCan(b)`
  // short-circuits, which makes the second one a CONDITIONAL hook call and
  // trips react-hooks/rules-of-hooks in the zero-warning build — recorded in
  // docs/AGENT_LEARNINGS.md after it cost somebody twenty minutes.
  const canApprove = useCan('timesheets', 'approve');
  const canManageAll = useCan('timesheets', 'manage_all');
  const canDecide = canApprove || canManageAll;

  const mine = useQuery({
    queryKey: ['time-off', 'mine'],
    queryFn: () => get('/time-off?scope=mine'),
    retry: false,
  });
  const queue = useQuery({
    queryKey: ['time-off', 'pending'],
    queryFn: () => get('/time-off?status=pending'),
    retry: false,
    enabled: canDecide,
  });

  const [asking, setAsking] = useState(false);
  const [kind, setKind] = useState('vacation');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [hours, setHours] = useState(8);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [denying, setDenying] = useState(null);
  const [denyNote, setDenyNote] = useState('');

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['time-off'] });
  };

  const run = async (tag, fn, done) => {
    setBusy(tag); setError(null);
    try {
      await fn();
      setNote(done);
      refresh();
      return true;
    } catch (e) {
      // The server's own sentence, not a generic one: every refusal from
      // routes/timeOff.js is written to be shown ("There is already a request
      // covering 2026-10-05.").
      setError(describeError(e, 'That did not save.'));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const ask = async () => {
    const ok = await run('ask', () => post('/time-off', {
      kind, start_date: start, end_date: end || start, hours_per_day: hours,
      reason: reason.trim() || undefined,
    }), 'Asked. Somebody will answer it.');
    if (ok) {
      setAsking(false);
      setStart(''); setEnd(''); setReason('');
    }
  };

  const decide = (id, status, decided_note) => run(
    `decide:${id}`,
    () => post(`/time-off/${id}/decide`, { status, decided_note }),
    status === 'approved' ? 'Approved.' : 'Turned down.',
  );

  const withdraw = (id) => run(`drop:${id}`, () => del(`/time-off/${id}`), 'Withdrawn.');

  const myRows = Array.isArray(mine.data) ? mine.data : [];
  const queueRows = Array.isArray(queue.data) ? queue.data : [];
  // Never answer your own, so never offer it. The server refuses it too.
  const toAnswer = queueRows.filter((r) => !myRows.some((m) => m.id === r.id));

  return (
    <div className="to">
      {error ? <Notice tone="stop" data-time-off-error>{error}</Notice> : null}
      {note ? <Notice tone="ok" data-time-off-note>{note}</Notice> : null}

      <div className="to-top">
        <FwButton variant="primary" size="lg" full onClick={() => { setAsking(true); setNote(null); }}>
          Ask for time off
        </FwButton>
      </div>

      {canDecide ? (
        <>
          <SectionLabel>Waiting for you</SectionLabel>
          {queue.isLoading ? <Skeleton rows={2} /> : null}
          {queue.error ? (
            <Notice tone="stop">{describeError(queue.error, 'The queue did not load.')}</Notice>
          ) : null}
          {!queue.isLoading && !queue.error && toAnswer.length === 0 ? (
            <EmptyState title="Nothing waiting">Nobody has asked for time off.</EmptyState>
          ) : null}
          <ul className="to-list">
            {toAnswer.map((r) => (
              <RequestRow key={r.id} r={r}>
                <FwButton
                  variant="primary"
                  data-time-off-approve={r.id}
                  loading={busy === `decide:${r.id}`}
                  onClick={() => decide(r.id, 'approved')}
                >
                  Approve
                </FwButton>
                <FwButton
                  variant="ghost"
                  data-time-off-deny={r.id}
                  onClick={() => { setDenying(r); setDenyNote(''); }}
                >
                  Turn down
                </FwButton>
              </RequestRow>
            ))}
          </ul>
        </>
      ) : null}

      <SectionLabel>Your time off</SectionLabel>
      {mine.isLoading ? <Skeleton rows={3} /> : null}
      {mine.error ? (
        <Notice tone="stop">{describeError(mine.error, 'Your requests did not load.')}</Notice>
      ) : null}
      {!mine.isLoading && !mine.error && myRows.length === 0 ? (
        <EmptyState title="No time off yet">
          Ask for a day and it will show up here with its answer.
        </EmptyState>
      ) : null}
      <ul className="to-list">
        {myRows.map((r) => (
          <RequestRow key={r.id} r={r}>
            {r.status === 'pending' ? (
              <FwButton
                variant="quiet"
                data-time-off-withdraw={r.id}
                loading={busy === `drop:${r.id}`}
                onClick={() => withdraw(r.id)}
              >
                Withdraw
              </FwButton>
            ) : null}
          </RequestRow>
        ))}
      </ul>

      <Sheet
        open={asking}
        title="Ask for time off"
        onClose={() => setAsking(false)}
        footer={(
          <FwButton
            size="lg"
            full
            onClick={ask}
            loading={busy === 'ask'}
            disabled={!start}
          >
            Ask
          </FwButton>
        )}
      >
        <div className="to-form">
          <FwField label="What is it" htmlFor="to-kind">
            <FwSelect id="to-kind" value={kind} onChange={(e) => setKind(e.target.value)}>
              {TIME_OFF_KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
            </FwSelect>
          </FwField>
          {/* type="date" so a phone gives its own picker. The value is the
              YYYY-MM-DD the server stores, with no timezone in it — the same
              reason the column is TEXT and not a datetime. */}
          <FwField label="First day" htmlFor="to-start" required>
            <FwInput id="to-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </FwField>
          <FwField
            label="Last day"
            htmlFor="to-end"
            hint="Leave empty for a single day."
          >
            <FwInput id="to-end" type="date" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} />
          </FwField>
          <HoursField
            id="to-hours"
            value={hours}
            onChange={setHours}
            label="Hours a day"
            hint="What a normal day is for you. The office uses this when they work out the week."
            required
          />
          <FwField label="Anything to add" htmlFor="to-reason">
            <FwTextarea
              id="to-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Optional"
              rows={3}
            />
          </FwField>
          {start && (end || start) ? (
            <div className="to-form__summary fw-num" data-time-off-summary>
              {dayCount(start, end || start)} {dayCount(start, end || start) === 1 ? 'day' : 'days'}
              {' · '}{clockLabel(hours)} a day
            </div>
          ) : null}
        </div>
      </Sheet>

      <Sheet
        open={!!denying}
        title="Turn it down"
        onClose={() => setDenying(null)}
        footer={(
          <FwButton
            size="lg"
            full
            variant="stop"
            loading={busy === `decide:${denying && denying.id}`}
            disabled={!denyNote.trim()}
            onClick={async () => {
              const ok = await decide(denying.id, 'denied', denyNote.trim());
              if (ok) setDenying(null);
            }}
          >
            Turn it down
          </FwButton>
        )}
      >
        {denying ? (
          <div className="to-form">
            <div className="to-form__who">
              {denying.user_name} · {rangeLabel(denying.start_date, denying.end_date)}
            </div>
            {/* Required, and the server requires it too. Somebody who finds out
                they are working on Friday should find out why at the same time. */}
            <FwField
              label="Why"
              htmlFor="to-deny-note"
              required
              hint="They see this. Saying nothing is not an option."
            >
              <FwTextarea
                id="to-deny-note"
                value={denyNote}
                onChange={(e) => setDenyNote(e.target.value)}
                rows={3}
              />
            </FwField>
          </div>
        ) : null}
      </Sheet>
    </div>
  );
}
